import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import type { SwiperRef } from 'swiper/react';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css/pagination';

import {
  getBibleBoostCreateVideoPreview,
  getMemoryBoostCreateVideoPreview,
  Images,
} from '../../../constants/images';
import {
  getBibleBoostCreateVideo,
  getMemoryBoostCreateVideo,
} from '../../../constants/videos';
import { usePopup } from '../../../context';
import { TranslationLocale } from '../../../i18n';
import { WebBoosterType } from '../../../models/UserStore';
import { getUiLocale } from '../../../utils/i18n';
import { VideoPlayer } from '../VideoPlayer';

import 'swiper/css';
import styles from './CreateMemoryGameBannerCarousel.module.css';

type CreateMemoryGameBannerCarouselProps = {
  webBoosterType: WebBoosterType | undefined;
};

export const CreateMemoryGameBannerCarousel = ({
  webBoosterType,
}: CreateMemoryGameBannerCarouselProps) => {
  const { i18n } = useTranslation();

  const carousels = [
    Images.SpellingJOYBySyllables_01,
    Images.SpellingJOYByLetters_01,
    Images.QuickMemoryJOY_01,
    Images.QuickMemoryJOY_02,
    Images.QuickMemoryJOY_03,
    Images.QuickMemoryJOY_04,
    Images.QuickHandwritingJOY_01,
    Images.QuickHandwritingJOY_02,
  ];
  const { showPopup } = usePopup();
  const swiperRef = useRef<SwiperRef>(null);

  const getVideo =
    webBoosterType === WebBoosterType.Normal
      ? getMemoryBoostCreateVideo
      : getBibleBoostCreateVideo;
  const getVideoPreview =
    webBoosterType === WebBoosterType.Normal
      ? getMemoryBoostCreateVideoPreview
      : getBibleBoostCreateVideoPreview;

  const onClickVideo = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    swiperRef.current?.swiper?.autoplay?.stop();
    showPopup(
      <VideoPlayer
        url={getVideo(getUiLocale(i18n.language as TranslationLocale))}
        // thumbnailUrl={getVideoPreview(
        //   getUiLocale(i18n.language as TranslationLocale),
        // )}
        playIcon={<></>}
        playing={true}
        muted={false}
      />,
      {
        maxWidth: '100%',
        maxHeight: '100%',
        aspectRatio: '16/9',
        withCross: true,
        backdropCallback: () => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
          swiperRef.current?.swiper?.autoplay?.start();
        },
      },
    );
  }, [getVideo, i18n.language, showPopup]);

  return (
    <Swiper
      ref={swiperRef}
      className={styles.swiper}
      //
      spaceBetween={30}
      centeredSlides={true}
      autoplay={{
        delay: 2500,
        disableOnInteraction: false,
      }}
      pagination={{
        clickable: true,
      }}
      navigation={true}
      modules={[Autoplay, Pagination, Navigation]}
    >
      <SwiperSlide>
        <div className={styles.imageContainer} onClick={onClickVideo}>
          <img
            className={styles.videoThumbnail}
            src={getVideoPreview(
              getUiLocale(i18n.language as TranslationLocale),
            )}
          />
        </div>
      </SwiperSlide>
      {carousels.map((image, index) => (
        <SwiperSlide key={index}>
          <div className={styles.imageContainer}>
            <img src={image} className={styles.slideContent} />
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};
